import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { useCurrency } from "../context/CurrencyContext";
import { cardTotalValue } from "../lib/conditions";

export default function CompleteSaleDialog({ card, open, onOpenChange, onCompleted }) {
  const { rate, symbol, format } = useCurrency();
  const [price, setPrice] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !card) return;
    const usd = card.sold_price ?? card.offer_price;
    setPrice(usd != null ? (usd * rate).toFixed(2) : "");
  }, [open, card, rate]);

  const confirm = async () => {
    const parsed = parseFloat(price);
    if (isNaN(parsed) || parsed <= 0) {
      toast.error("Enter the price the card sold for");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/cards/${card.id}/complete-sale`, { sold_price: parsed / (rate || 1) });
      toast.success(`Sale logged — "${card.name}" moved to your Sales Ledger`);
      onOpenChange(false);
      onCompleted?.(card.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't complete the sale");
    } finally {
      setSaving(false);
    }
  };

  if (!card) return null;
  const marketValue = cardTotalValue(card);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="complete-sale-dialog" className="bg-[#111113] border-white/10 text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BadgeCheck className="w-5 h-5 text-emerald-400" /> Complete sale
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            "{card.name}" will leave your binder and be logged in your private Sales Ledger.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-zinc-400">Final sold price</span>
          <div className="flex items-center gap-1">
            <span className="text-zinc-500 font-mono-plex">{symbol}</span>
            <Input
              data-testid="complete-sale-price-input"
              type="number"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="0.00"
              className="w-28 bg-black/40 border-white/10 text-right font-mono-plex font-semibold text-emerald-300"
            />
          </div>
        </div>
        {marketValue != null && (
          <p className="text-[11px] text-zinc-600">
            Current market value: <span className="font-mono-plex text-zinc-400">{format(marketValue)}</span> — the ledger tracks how your sale compares.
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            data-testid="complete-sale-cancel"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-full text-zinc-400 hover:text-white"
          >
            Cancel
          </Button>
          <Button
            data-testid="complete-sale-confirm"
            onClick={confirm}
            disabled={saving}
            className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"
          >
            {saving ? "Logging..." : "Log sale"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
